import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  createCategoryPricing,
  createSpecificProductPricing,
  deleteCategoryPricing,
  deleteProductTypeKeywords,
  deleteSpecificProductPricing,
  getCategoryPricing,
  getPricingStatistics,
  getProductTypeKeywords,
  getSpecificProductPricing,
  updateCategoryPricing,
  updateProductTypeKeywords,
  updateSpecificProductPricing,
  uploadCategoryKeywords,
  uploadProductTypeKeywords,
} from "../services/pricingService";
import {
  CategoryPricing,
  ProductTypeKeywords,
  SpecificProductPricing,
  UpdateCategoryPricingRequest,
  UpdateProductPricingRequest,
  UpdateProductTypeKeywordsRequest,
} from "../types/admin";

// Query Keys
export const pricingKeys = {
  all: ["pricing"] as const,
  categories: () => [...pricingKeys.all, "categories"] as const,
  products: () => [...pricingKeys.all, "products"] as const,
  statistics: () => [...pricingKeys.all, "statistics"] as const,
  productTypeKeywords: () => [...pricingKeys.all, "productTypeKeywords"] as const,
  category: (id: string) => [...pricingKeys.categories(), id] as const,
  product: (id: string) => [...pricingKeys.products(), id] as const,
};

// Category Pricing Hooks
export const useCategoryPricing = (pageNumber?: number, pageSize?: number, search?: string) => {
  return useQuery({
    queryKey: pageNumber && pageSize 
      ? [...pricingKeys.categories(), pageNumber, pageSize, search || ""] 
      : pricingKeys.categories(),
    queryFn: () => getCategoryPricing(pageNumber, pageSize, search),
    staleTime: 30 * 1000, // 30 seconds
  });
};

export const useSpecificProductPricing = (pageNumber?: number, pageSize?: number, search?: string) => {
  return useQuery({
    queryKey: pageNumber && pageSize 
      ? [...pricingKeys.products(), pageNumber, pageSize, search || ""] 
      : pricingKeys.products(),
    queryFn: () => getSpecificProductPricing(pageNumber, pageSize, search),
    staleTime: 30 * 1000, // 30 seconds
  });
};

export const usePricingStatistics = () => {
  return useQuery({
    queryKey: pricingKeys.statistics(),
    queryFn: getPricingStatistics,
    staleTime: 60 * 1000, // 1 minute
  });
};

// Mutation Hooks
export const usePricingMutations = () => {
  const queryClient = useQueryClient();

  const createCategoryPricingMutation = useMutation({
    mutationFn: createCategoryPricing,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pricingKeys.categories() });
      queryClient.invalidateQueries({ queryKey: pricingKeys.statistics() });
    },
  });

  const updateCategoryPricingMutation = useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: UpdateCategoryPricingRequest;
    }) => updateCategoryPricing(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pricingKeys.categories() });
      queryClient.invalidateQueries({ queryKey: pricingKeys.statistics() });
    },
  });

  const deleteCategoryPricingMutation = useMutation({
    mutationFn: deleteCategoryPricing,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pricingKeys.categories() });
      queryClient.invalidateQueries({ queryKey: pricingKeys.statistics() });
    },
  });

  const createProductPricingMutation = useMutation({
    mutationFn: createSpecificProductPricing,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pricingKeys.products() });
      queryClient.invalidateQueries({ queryKey: pricingKeys.statistics() });
    },
  });

  const updateProductPricingMutation = useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: UpdateProductPricingRequest;
    }) => updateSpecificProductPricing(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pricingKeys.products() });
      queryClient.invalidateQueries({ queryKey: pricingKeys.statistics() });
    },
  });

  const deleteProductPricingMutation = useMutation({
    mutationFn: deleteSpecificProductPricing,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pricingKeys.products() });
      queryClient.invalidateQueries({ queryKey: pricingKeys.products() });
      queryClient.invalidateQueries({ queryKey: pricingKeys.statistics() });
    },
  });

  const uploadCategoryKeywordsMutation = useMutation({
    mutationFn: ({ id, file }: { id: string; file: File }) => uploadCategoryKeywords(id, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pricingKeys.categories() });
    },
  });

  return {
    createCategoryPricing: createCategoryPricingMutation,
    updateCategoryPricing: updateCategoryPricingMutation,
    deleteCategoryPricing: deleteCategoryPricingMutation,
    createProductPricing: createProductPricingMutation,
    updateProductPricing: updateProductPricingMutation,
    deleteProductPricing: deleteProductPricingMutation,
    uploadCategoryKeywords: uploadCategoryKeywordsMutation,
  };
};

// Optimistic Updates
export const useOptimisticPricingUpdate = () => {
  const queryClient = useQueryClient();

  const updateCategoryPricingOptimistically = (
    id: string,
    updates: Partial<CategoryPricing>,
  ) => {
    queryClient.setQueryData(pricingKeys.categories(), (oldData: any) => {
      if (!oldData?.data) return oldData;
      return {
        ...oldData,
        data: oldData.data.map((pricing: CategoryPricing) =>
          pricing.id === id ? { ...pricing, ...updates } : pricing,
        ),
      };
    });
  };

  const updateProductPricingOptimistically = (
    id: string,
    updates: Partial<SpecificProductPricing>,
  ) => {
    queryClient.setQueryData(pricingKeys.products(), (oldData: any) => {
      if (!oldData?.data) return oldData;
      return {
        ...oldData,
        data: oldData.data.map((pricing: SpecificProductPricing) =>
          pricing.id === id ? { ...pricing, ...updates } : pricing,
        ),
      };
    });
  };

  return {
    updateCategoryPricingOptimistically,
    updateProductPricingOptimistically,
  };
};

// ─── ProductTypeKeywords Hooks ────────────────────────────────────────────────

export const useProductTypeKeywords = () => {
  return useQuery({
    queryKey: pricingKeys.productTypeKeywords(),
    queryFn: getProductTypeKeywords,
    staleTime: 60 * 1000, // 1 minute
  });
};

export const useProductTypeKeywordsMutations = () => {
  const queryClient = useQueryClient();

  const updateKeywordsMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateProductTypeKeywordsRequest }) =>
      updateProductTypeKeywords(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pricingKeys.productTypeKeywords() });
    },
  });

  const uploadProductTypeKeywordsMutation = useMutation({
    mutationFn: ({ id, file }: { id: number; file: File }) => uploadProductTypeKeywords(id, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pricingKeys.productTypeKeywords() });
    },
  });

  const deleteKeywordsMutation = useMutation({
    mutationFn: (id: number) => deleteProductTypeKeywords(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pricingKeys.productTypeKeywords() });
    },
  });

  return {
    updateKeywords: updateKeywordsMutation,
    uploadKeywords: uploadProductTypeKeywordsMutation,
    deleteKeywords: deleteKeywordsMutation,
  };
};
